/* eslint-disable @typescript-eslint/dot-notation */
import { IImportHandler } from '../import-handler.interface';
import { ErrorCodes, FileModel, getTypeofProperty } from '@shared';
import { WorkspaceDatabaseKeys, RefTableSchemaBuilder, RefTablesStorageService } from '@database/workspace';
import {
	JsonFileDataParser,
	TableInfoModel,
	TableInfoRepository,
	TableTypeEnum,
	WorkspaceRepository
} from '@database/central';
import { LoggingService } from '@shared/logging';

export class ConfigDatabaseRefTableHandler implements IImportHandler {
	private next: IImportHandler;

	public constructor(
		private jsonFileDataParser: JsonFileDataParser,
		private loggingService: LoggingService,
		private tableInfoRepository: TableInfoRepository,
		private workspaceRepository: WorkspaceRepository,
		private refTablesStorageService: RefTablesStorageService
	) {}

	public async cleanup(model: FileModel): Promise<void> {
		await this.workspaceRepository.remove(model.workFileId);
	}

	public async handle(model: FileModel): Promise<void> {
		let refTableDictionary: { [key: number]: any[] };

		try {
			refTableDictionary = this.jsonFileDataParser.extractReferenceTablesDict();
			await this.saveRefTableNames(Object.keys(refTableDictionary), model.workFileId);
		} catch (error) {
			this.loggingService.error(ErrorCodes.ConfigDatabaseRefTableHandler, error);
			await this.cleanup(model);
			throw new Error('Grouping of reference table data failed');
		}
		console.log("RefTables: ", refTableDictionary);
		
		try {
			for (const key in refTableDictionary) {
				if (refTableDictionary.hasOwnProperty(key)) {
					if (WorkspaceDatabaseKeys.excludedItems.includes(key.toUpperCase())) {
						continue;
					}

					// Check if the table already exists
					const exists = await this.refTablesStorageService.get(model.workFileId, key.toLowerCase())
						.then(() => true)
						.catch(() => false);
					

					
					if (exists) {
						await this.updateRefTable(model.workFileId, key.toLowerCase(), refTableDictionary[key]);
					} else {
						await this.insertRefTable(model.workFileId, key.toLowerCase(), refTableDictionary[key]);
					}
				}
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.ConfigDatabaseRefTableHandler, error);
			await this.cleanup(model);
			throw error;
		}

		if (this.next) {
			await this.next.handle(model);
		}
	}

	public async setNext(nextHandler: IImportHandler) {
		this.next = nextHandler;
	}

	private buildSchema(key: string, refTableItems: any[]): { [name: string]: any } {
		const schemaBuilder = new RefTableSchemaBuilder();

		// eslint-disable-next-line guard-for-in
		for (const property in refTableItems[0]) {
			// ToDo: maybe remove 'reference_table_name', we already have that id as collection name
			schemaBuilder.addRefTableProperty(property, getTypeofProperty(refTableItems[0], property));
		}

		const collections: { [name: string]: any } = {};

		collections[key.toLowerCase()] = { schema: schemaBuilder.build() };

		return collections;
	}

	private async saveRefTableNames(tableNames: string[], workFileId: string): Promise<void> {
		const tableInfos: TableInfoModel[] = tableNames
			.filter((item) => !WorkspaceDatabaseKeys.excludedItems.includes(item.toUpperCase()))
			.map(
				(name) =>
					new TableInfoModel({
						name,
						tableType: TableTypeEnum.RefTable,
						workFileId
					})
			);

		await this.tableInfoRepository.bulkInsert(tableInfos);
	}

	private async insertRefTable(workFileId: string, key: string, refTableItems: any[]): Promise<void> {
		try {
			const collections = this.buildSchema(key, refTableItems);

			// Insert the new reference table
			for (const collectionKey in collections) {
				if (collections.hasOwnProperty(collectionKey)) {
					await this.refTablesStorageService.set(workFileId, collectionKey, refTableItems);
				}
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.ConfigDatabaseRefTableHandler, error);
			throw new Error('Failed to insert reference table data');
		}
	}

	private async updateRefTable(workFileId: string, key: string, refTableItems: any[]): Promise<void> {
		try {
			const collections = this.buildSchema(key, refTableItems);

			for (const collectionKey in collections) {
				if (collections.hasOwnProperty(collectionKey)) {
					await this.refTablesStorageService.set(workFileId, collectionKey, refTableItems);
				}
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.ConfigDatabaseRefTableHandler, error);
			throw new Error('Failed to update reference table data');
		}
	}
}
